import {
  IPortfolioPerformanceAccountReport,
  IPortfolioPerformanceHistorical,
} from './portfolio-performance.types';
import { getLastDayPv } from './compute-daily-stats';

const MONTH_ORDER = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
];

// Every account's Sharpe/Sortino/MaxDD figure — real or reference — is
// scoped to this single year, so the Overview table's "(2026)" column
// headers are honest for every row (see portfolio-performance-overview-table).
// Real accounts are additionally restricted to this window because prior
// years aren't reconciled across accounts yet (see the account card's own
// TODO on its `historical` useMemo); reference/benchmark reports have no
// such data-quality issue and could show ITD stats, but are pinned to the
// same year anyway purely for cross-row consistency in that one table.
export const STATS_YEAR = '2026';

// Builds the historical slice `computeDailyStats` should run over for a
// given report, used identically by both the account card (its own Sharpe/
// Sortino/MaxDD chips) and the Overview table (its Sharpe/Sortino/MaxDD
// columns) — passing the SAME computed value to both, not just the same
// formula, so the two can never drift apart.
//
// Real accounts: today's entry is injected from the live daily_return/
// mtd_return metrics before slicing to STATS_YEAR, since the backend's
// historical.days only ever contains CLOSED days (today's key is simply
// absent until finalized a day later) — see portfolio-performance-account-
// card's original comment for the full rationale.
//
// Reference accounts: no injection needed HERE — today's live day is already
// injected further upstream, into report.historical itself, by
// build-reference-report.ts's injectLiveDay (candlesticks only ever cover
// CLOSED days, so without that upstream injection today's key would be
// absent the same way it is for real accounts). This is just the plain
// STATS_YEAR slice of whatever multi-year history results.
export function buildStatsHistorical(
  report: IPortfolioPerformanceAccountReport | null | undefined,
  isReference: boolean,
): IPortfolioPerformanceHistorical | null | undefined {
  if (!report?.historical) return report?.historical;

  const yearData = report.historical[STATS_YEAR];
  if (!yearData) return {};

  if (isReference) {
    return { [STATS_YEAR]: yearData };
  }

  const now = new Date();
  const currentMonthKey = MONTH_ORDER[now.getUTCMonth()];
  const todayKey = String(now.getUTCDate()).padStart(2, '0');
  const currentMonth = yearData.months[currentMonthKey];

  const lastClosePv = getLastDayPv(report.historical);
  const todayPv =
    lastClosePv !== null && report.dailyReturnRaw !== null
      ? lastClosePv * (1 + report.dailyReturnRaw)
      : undefined;

  const months =
    now.getUTCFullYear() === Number(STATS_YEAR) &&
    report.dailyReturnRaw !== null &&
    currentMonth &&
    !(todayKey in currentMonth.days)
      ? {
          ...yearData.months,
          [currentMonthKey]: {
            ...currentMonth,
            days: {
              ...currentMonth.days,
              [todayKey]: { return: report.dailyReturnRaw, pv: todayPv },
            },
            monthly_return: report.mtdReturnRaw ?? currentMonth.monthly_return,
          },
        }
      : yearData.months;

  return {
    [STATS_YEAR]: {
      ...yearData,
      months,
      annual_return: yearData.annual_return ?? report.ytdReturnRaw,
    },
  };
}
